import { FC, useState } from "react";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import type { DepositEntry } from "./AmountStep";
import { DepositStepHeader } from "./DepositStepHeader";
import { RulesView } from "../RulesView";
import {
  beginXSignIn,
  competitionSessionKey,
  signOutX,
  useCompetitionSession,
} from "@/hooks/useCompetitionAuth";
import { useQueryClient } from "@tanstack/react-query";

interface ReviewStepProps {
  entries: DepositEntry[];
  error: string | null;
  headOnline: boolean;
  agreedRules: boolean;
  onAgreedRulesChange: (agreed: boolean) => void;
  showHandle: boolean;
  onShowHandleChange: (show: boolean) => void;
  onConfirm: () => void;
  onBack?: () => void;
  /** Head-config-derived deposit-to-funds estimate in seconds; the head's
   * maturity window dominates it (minutes on preview, ~an hour on mainnet). */
  estimatedSeconds?: number | null;
}

function fmtEstimate(secs: number): string {
  if (secs < 90) return "~1 min";
  if (secs < 3600) return `~${Math.ceil(secs / 60)} min`;
  const h = Math.floor(secs / 3600);
  const m = Math.round((secs % 3600) / 60);
  return m > 5 ? `~${h}h ${m}m` : `~${h}h`;
}

function truncateAddress(addr: string): string {
  if (addr.length <= 20) return addr;
  return `${addr.slice(0, 9)}....${addr.slice(-8)}`;
}

function assetName(assetId: string): string {
  if (assetId === "ada.lovelace") return "ADA";
  const hex = assetId.split(".")[1] ?? "";
  try {
    return Buffer.from(hex, "hex").toString("utf-8") || hex.slice(0, 8);
  } catch {
    return hex.slice(0, 8);
  }
}

export const ReviewStep: FC<ReviewStepProps> = ({
  entries,
  error,
  headOnline,
  agreedRules,
  onAgreedRulesChange,
  showHandle,
  onShowHandleChange,
  onConfirm,
  onBack,
  estimatedSeconds,
}) => {
  const { mainAddress } = useWalletObserver();
  const address = mainAddress ?? "";
  const { data: session, isLoading: sessionLoading } = useCompetitionSession();
  const queryClient = useQueryClient();
  const handleSignOut = async () => {
    await signOutX();
    queryClient.invalidateQueries({ queryKey: competitionSessionKey });
  };
  const [rulesOpen, setRulesOpen] = useState(false);
  const authed = session?.authenticated === true;
  const canConfirm = headOnline && authed && agreedRules;

  return (
    <div className="flex flex-col gap-12 items-center w-full">
      {/* Header */}
      <DepositStepHeader
        title="Confirm your deposit"
        description="Your funds will be available after network confirmation."
      />

      {/* Details + CTA + highlight */}
      <div className="flex flex-col gap-3 w-full">
        {/* Details card */}
        <div className="sr-card w-full p-3 sm:p-4">
          <div className="flex flex-col gap-2">
            {entries.filter((e) => Number(e.amount) > 0).map((entry, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-sr-pink-muted">{i === 0 ? "Deposit" : ""}</span>
                <span className="text-sm text-white">
                  {Number(entry.amount).toLocaleString()} {assetName(entry.assetId)}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between">
              <span className="text-sm text-sr-pink-muted">From</span>
              <span className="text-sm text-sr-pink-muted">
                {truncateAddress(address)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-sr-pink-muted">Network</span>
              <span className="text-sm text-sr-pink-muted">
                Cardano L1 → Gummiworm L2
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-sr-pink-muted">Est. time</span>
              <span className="text-sm text-white">
                {estimatedSeconds ? fmtEstimate(estimatedSeconds) : "a few min"}
              </span>
            </div>
          </div>
        </div>

        {/* Competition registration: X identity + rules agreement */}
        <div className="sr-card w-full p-3 sm:p-4">
          <div className="flex flex-col gap-3">
            {authed ? (
              <div className="flex items-center justify-between">
                <span className="text-sm text-sr-pink-muted">Verified as</span>
                <span className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white">
                    @{session?.handle}
                  </span>
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="text-xs text-sr-neutral-500 underline decoration-dotted underline-offset-2 hover:text-white"
                  >
                    sign out
                  </button>
                </span>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <button
                  className="sr-surface-pill w-full rounded-full px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/[0.06]"
                  onClick={() => {
                    // The OAuth redirect reloads the SPA; stash the deposit
                    // so the return trip resumes at this step.
                    try {
                      sessionStorage.setItem(
                        "sr:resumeDeposit",
                        JSON.stringify({ entries }),
                      );
                    } catch {
                      /* private mode; the user redoes two steps */
                    }
                    beginXSignIn();
                  }}
                  disabled={sessionLoading}
                >
                  {sessionLoading ? "Checking session…" : "Sign in with X"}
                </button>
                <p className="text-[11px] leading-relaxed text-sr-neutral-500">
                  Used to verify one entry per person. We request read-only
                  access and store only your handle and account age.
                </p>
              </div>
            )}
            <label className="flex cursor-pointer items-start gap-2.5">
              <input
                type="checkbox"
                checked={agreedRules}
                onChange={(e) => onAgreedRulesChange(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-sr-lemon-muted,#eab308)]"
              />
              <span className="text-xs leading-relaxed text-sr-pink-muted">
                I have read and agree to the{" "}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setRulesOpen(true);
                  }}
                  className="text-white underline decoration-dotted underline-offset-2 hover:text-sr-lemon-muted"
                >
                  Official Competition Rules
                </button>
                , including the one-account-per-person requirement.
              </span>
            </label>
            {authed && (
              <label className="flex cursor-pointer items-start gap-2.5">
                <input
                  type="checkbox"
                  checked={showHandle}
                  onChange={(e) => onShowHandleChange(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-sr-lemon-muted,#eab308)]"
                />
                <span className="text-xs leading-relaxed text-sr-pink-muted">
                  Show my handle{session?.handle ? ` (@${session.handle})` : ""} on the public
                  leaderboard. Optional — you can change this later.
                </span>
              </label>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="flex w-full items-center gap-3">
          {onBack && (
            <button
              className="sr-surface-pill flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-full text-sr-neutral-300 transition-colors hover:text-white"
              onClick={onBack}
              aria-label="Go back"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}
          <button
            className="sr-btn-primary flex-1"
            disabled={!canConfirm}
            onClick={onConfirm}
          >
            {!headOnline
              ? "Unavailable"
              : !authed
                ? "Sign in with X to continue"
                : !agreedRules
                  ? "Agree to the rules to continue"
                  : "Confirm and deposit"}
          </button>
        </div>

        {/* What happens next */}
        <div className="sr-highlight-box w-full">
          <div className="flex flex-col gap-2">
            <p className="text-base font-medium text-sr-pink-light">
              What happens next?
            </p>
            <ol className="text-xs text-sr-pink-bright list-decimal ml-4 flex flex-col gap-1">
              <li>Wait for network confirmation</li>
              <li>Your funds will be available shortly!</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Head offline warning */}
      {!headOnline && (
        <div className="w-full rounded-xl bg-sr-tangerine/10 p-3">
          <p className="text-xs text-sr-tangerine">
            Sugar Rush is offline right now. Please try again later.
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="w-full rounded-xl bg-sr-salmon/10 p-3">
          <p className="text-xs text-sr-salmon">{error}</p>
        </div>
      )}

      {/* In-overlay rules reader, so reviewing the rules never loses the
          deposit flow's state (the SPA has no routes to come back to). */}
      {rulesOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && setRulesOpen(false)}
        >
          <div className="relative w-full max-w-3xl">
            <button
              onClick={() => setRulesOpen(false)}
              className="sticky top-2 z-10 ml-auto mr-2 block rounded-full border border-white/[0.06] bg-sr-surface px-4 py-2 text-sm text-sr-neutral-300 transition-colors hover:text-white"
            >
              Close
            </button>
            <RulesView />
          </div>
        </div>
      )}
    </div>
  );
};
